import Vue from 'vue';
import Router from 'vue-router';
import store from '../store';

// Reusable Components
import Wrapper from '@/components/base/Wrapper';
import StatusScreen from '@/components/StatusScreen';
import SuccessPage from '@/pages/Success';

// Routes
import AdminCerdas from './routes/AdminCerdas';
import AdminSchool from './routes/AdminSchool';
import Teacher from './routes/Teacher';
import Parent from './routes/Parent';
import Student from './routes/Student';
import SchoolAdvisor from './routes/SchoolAdvisor';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    // Initial Page
    {
      path: '',
      redirect: () => {
        const { roleId, verified } = store.getters['Auth/user'];
        const redirect = ['ReportAdminIndex', 'MasterUserSchool', 'ParentCourseIndex', 'TeacherClassIndex', 'StudentClassIndex', 'StudentIndex'];
        if (roleId && verified) {
          return { name: redirect[roleId - 1] };
        } else if (roleId && !verified) {
          return { name: 'AuthChangePassword' };
        } else {
          return { name: 'AuthLogin' };
        }
      }
    },

    // Auth Page
    {
      path: '/auth',
      component: Wrapper,
      children: [
        {
          path: 'login',
          name: 'AuthLogin',
          meta: { rule: 'isAll', layout: 'empty' },
          component: () => import(/* webpackChunkName: "auth-login" */ '@/pages/auth/Login')
        },
        {
          path: 'register',
          component: Wrapper,
          children: [
            {
              path: '',
              name: 'AuthRegister',
              meta: { rule: 'isAll' },
              component: () => import(/* webpackChunkName: "auth-forgot-pass" */ '@/pages/auth/Register')
            },
            {
              path: 'teacher',
              name: 'AuthRegisterTeacher',
              meta: { rule: 'isAll' },
              component: () => import(/* webpackChunkName: "auth-forgot-pass" */ '@/pages/auth/Register_teacher')
            },
            {
              path: 'student',
              name: 'AuthRegisterStudent',
              meta: { rule: 'isAll' },
              component: () => import(/* webpackChunkName: "auth-forgot-pass" */ '@/pages/auth/Register_student')
            },
            {
              path: '',
              component: Wrapper,
              children: [
                {
                  path: 'form-teacher',
                  name: 'AuthRegisterTeacherForm',
                  meta: { rule: 'isAll' },
                  component: () => import(/* webpackChunkName: "auth-forgot-pass" */ '@/pages/auth/FormTeacherStudent'),
                  props: { type: 'teacher' }
                },

                {
                  path: 'form-student',
                  name: 'AuthRegisterStudentForm',
                  meta: { rule: 'isAll' },
                  component: () => import(/* webpackChunkName: "auth-forgot-pass" */ '@/pages/auth/FormTeacherStudent'),
                  props: { type: 'student' }
                },
                {
                  path: 'success',
                  name: 'AuthRegisterSuccess',
                  component: StatusScreen,
                  meta: { rule: 'isAll' },
                  props: {
                    type: 'success-send',
                    title: 'success.register_title',
                    subtitle: 'success.register_content',
                    back: () => router.push({ name: 'AuthLogin', replace: true })
                  }
                }
              ]
            }
          ]
        },
        {
          path: 'forgot-password',
          component: Wrapper,
          children: [
            {
              path: '',
              name: 'AuthForgotPass',
              meta: { rule: 'isAll' },
              component: () => import(/* webpackChunkName: "auth-forgot-pass" */ '@/pages/auth/ForgotPass')
            },
            {
              path: 'success',
              name: 'AuthForgotPassSuccess',
              component: StatusScreen,
              meta: { rule: 'isAll' },
              props: {
                type: 'success-send',
                title: 'success.reset_password_title',
                subtitle: 'success.reset_password_content',
                back: () => router.push({ name: 'AuthLogin', replace: true })
              }
            }
          ]
        },
        {
          path: 'reset-password',
          component: Wrapper,
          children: [
            {
              path: '',
              name: 'AuthResetPass',
              component: () => import(/* webpackChunkName: "auth-reset-pass" */ '@/pages/auth/ResetPass'),
              meta: { rule: 'isAll' }
            },
            {
              path: 'success',
              name: 'AuthResetPassSuccess',
              component: StatusScreen,
              meta: { rule: 'isAll' },
              props: {
                type: 'success',
                title: 'success.forgot_password_title',
                subtitle: 'success.forgot_password_content',
                back: () => router.push({ name: 'AuthLogin', replace: true })
              }
            }
          ]
        },
        {
          path: 'change-password',
          component: Wrapper,
          children: [
            {
              path: '',
              name: 'AuthChangePassword',
              component: () => import(/* webpackChunkName: "auth-reset-pass" */ '@/pages/auth/ChangePass'),
              meta: { rule: 'isAll' }
            }
          ]
        }
      ]
    },

    ...AdminCerdas,
    ...AdminSchool,
    ...Teacher,
    ...Student,
    ...Parent,
    ...SchoolAdvisor,

    // General :: Profile
    {
      path: '/profile',
      component: Wrapper,
      meta: { breadCrumb: 'menu.profile' },
      children: [
        {
          path: '',
          name: 'Profile',
          component: () => import(/* webpackChunkName: "profile-index" */ '@/pages/profile'),
          meta: { rule: 'isGeneral', layout: 'default', breadCrumb: 'menu.profile' }
        },
        {
          path: 'success',
          name: 'ProfileSuccessDeactivateUser',
          component: SuccessPage,
          meta: { rule: 'isGeneral' },
          props: { type: 'success-deactivate-user' }
        },
        {
          path: 'success',
          name: 'UserProfileUpdate',
          component: () => import(/* webpackChunkName: "success" */ '@/pages/Success'),
          meta: { rule: 'isGeneral' },
          props: { type: 'user-profile-update' }
        }
      ]
    },
    {
      path: '/account-information',
      name: 'AccountInformation',
      component: () => import(/* webpackChunkName: "profile-index" */ '@/pages/profile/AccountInformation.vue'),
      meta: { rule: 'isGeneral', layout: 'empty', breadCrumb: 'menu.deactive_account' }
    },

    // General :: Notification
    {
      path: '/notification',
      component: Wrapper,
      meta: { breadCrumb: 'form.notification' },
      children: [
        {
          path: '',
          name: 'NotifIndex',
          component: () => import(/* webpackChunkName: "notification-index" */ '@/pages/notification'),
          meta: { rule: 'isGeneral', layout: 'default' }
        },
        {
          path: 'personal/:id',
          component: Wrapper,
          meta: { breadCrumb: 'content.personal_birthday' },
          children: [
            {
              path: '',
              name: 'personalBirthday',
              component: () => import(/* webpackChunkName: "notification-index" */ '@/pages/notification/personalBirthday'),
              meta: { rule: 'isGeneral', layout: 'default' }
            }
          ]
        },
        {
          path: 'teacherquiz/:id',
          component: Wrapper,
          meta: { breadCrumb: 'content.detail_quitionnaire' },
          children: [
            {
              path: '',
              name: 'teacherQuiz',
              component: () => import(/* webpackChunkName: "notification-index" */ '@/pages/notification/FillTeacherQuis'),
              meta: { rule: 'isStudent', layout: 'default' }
            }
          ]
        }
      ]
    },
    // General :: Personal Birthday
    // {
    //   path: '',
    //   component: Wrapper,
    //   meta: { breadCrumb: 'form.notification' },
    //   children: [
    //     {
    //       path: 'notification/personal/:id',
    //       name: 'personalBirthday',
    //       component: () => import(/* webpackChunkName: "notification-index" */ '@/pages/notification/personalBirthday'),
    //       meta: { rule: 'isGeneral', layout: 'default' }
    //     },
    //     {
    //       path: 'notification/teacherquiz/:id',
    //       name: 'teacherQuiz',
    //       component: () => import(/* webpackChunkName: "notification-index" */ '@/pages/notification/FillTeacherQuis'),
    //       meta: { rule: 'isStudent', layout: 'default' }
    //     }
    //   ]
    // },

    // Status :: Not-Found
    {
      path: '*',
      name: 'NotFound',
      component: StatusScreen,
      meta: { rule: 'isAll' },
      props: {
        type: 'not-found',
        title: 'success.not_found_title',
        subtitle: 'success.not_found_content'
      }
    },

    // Status :: No Connection
    {
      path: '/no-connection',
      name: 'NoConnection',
      meta: { rule: 'isAll' },
      component: StatusScreen,
      props: {
        type: 'no-connection',
        title: 'success.no_connection_title',
        subtitle: 'success.no_connection_content'
      }
    },

    // Status :: Maintenance
    {
      path: '/maintenance',
      name: 'ServerMaintenance',
      component: StatusScreen,
      meta: { rule: 'isAll' },
      props: {
        type: 'maintenance',
        title: 'success.maintenance_title',
        subtitle: 'success.maintenance_content'
      }
    },

    // Status :: Server Busy
    {
      path: '/busy',
      name: 'ServerBusy',
      component: StatusScreen,
      meta: { rule: 'isAll' },
      props: {
        type: 'busy',
        title: 'success.server_busy_title',
        subtitle: 'success.server_busy_content'
      }
    },
    // Status :: testing
    {
      path: '/testing',
      name: 'Testing',
      component: () => import(/* webpackChunkName: "teacher-attendance-form" */ '@/components/Testing'),
      meta: { rule: 'isAll' }
    },
    // Status :: TA
    {
      path: '/terms',
      name: 'Terms',
      component: () => import(/* webpackChunkName: "teacher-attendance-form" */ '@/pages/ca-ta/Ta'),
      meta: { rule: 'isAll' }
    },
    // Status :: TA
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import(/* webpackChunkName: "teacher-attendance-form" */ '@/pages/ca-ta/Ca'),
      meta: { rule: 'isAll' }
    },
    {
      path: '/about',
      name: 'about',
      component: () => import(/* webpackChunkName: "teacher-attendance-form" */ '@/pages/ca-ta/Ca'),
      meta: { rule: 'isAll' }
    },
  ]
});

router.beforeEach((to, from, next) => {
  // Auth Guard Verified
  if (
    to.matched.some(record =>
      ['isGeneral', 'isCerdasAdmin', 'isSchoolAdmin', 'isSchoolAdminAdvisor', 'isTeacher', 'isParent', 'isStudent', 'isSchoolAdvisor'].includes(
        record.meta.rule
      )
    )
  ) {
    if (store.getters['Auth/currentVerified']) {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);

      const process = urlParams.get('process');
      const email = urlParams.get('email');
      if(process === 'deleteAccount' && email) 
        return next()

      if (
        store.getters['Auth/currentRole'] === 3 &&
        !['ChildList', 'ParentCourseIndex', 'Profile'].includes(to.name) &&
        !store.getters['Auth/student']
      ) {

        next({ name: 'ChildList' });
      } else {
        next();
      }
    } else {
      next({ name: 'AuthLogin' });
    }
  } else {
    next();
  }
});

export default router;
